import type { ReactNode } from 'react';

export interface SegmentedOption<T extends string> {
  id: T;
  label: string;
  icon?: ReactNode;
}

export interface SegmentedControlProps<T extends string> {
  options: SegmentedOption<T>[];
  value: T;
  onChange: (value: T) => void;
  ariaLabel: string;
  /** `segment`: joined buttons on a grey track (a view switch). `chips`: separate pills (quick filters). */
  variant?: 'segment' | 'chips';
}

/** A single choice among a few options, shown all at once. */
export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  ariaLabel,
  variant = 'segment',
}: SegmentedControlProps<T>) {
  if (variant === 'chips') {
    return (
      <div role="group" aria-label={ariaLabel} className="flex flex-wrap items-center gap-2">
        {options.map((o) => {
          const on = o.id === value;
          return (
            <button
              key={o.id}
              type="button"
              aria-pressed={on}
              onClick={() => onChange(o.id)}
              className={`min-h-11 sm:min-h-0 sm:h-8 px-3.5 rounded-full text-sm border transition-colors ${
                on
                  ? 'bg-gray-900 border-gray-900 text-gray-50'
                  : 'bg-white border-gray-200 text-gray-700 hover:border-gray-300'
              }`}
            >
              {o.label}
            </button>
          );
        })}
      </div>
    );
  }
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className="inline-flex items-center gap-0.5 p-0.5 bg-gray-100 rounded-lg"
    >
      {options.map((o) => {
        const on = o.id === value;
        return (
          <button
            key={o.id}
            type="button"
            aria-pressed={on}
            onClick={() => onChange(o.id)}
            className={`inline-flex items-center gap-1.5 min-h-11 sm:min-h-0 sm:h-8 px-3 rounded-md text-sm transition-all ${
              on
                ? 'bg-white text-gray-900 font-semibold shadow-sm'
                : 'text-gray-500 hover:text-gray-800'
            }`}
          >
            {o.icon}
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
